import clsx from "clsx";
import React, { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "wouter";

import { partTypeWheel } from "../../../constants/partTypes";
import { useLanguage } from "../../../i18n/useLanguage";
import { WheelPartItem } from "../../../queries/useSearchWheelsByVehicle";
import { useRelatedCartItems } from "../../../stores/hooks/useRelatedCartItems";
import { Button } from "../../../ui/Button/Button";
import { Image } from "../../../ui/Image/Image";
import { KeyValue } from "../../../ui/KeyValue/KeyValue";
import { Placeholder } from "../../../ui/Placeholder";
import { InfoIcon } from "../../../ui/icons/InfoIcon";
import { getPartFallbackSrc } from "../../../util/getPartFallbackSrc";
import { getPartName } from "../../../util/getPartName";
import { CartButton } from "../../Cart/CartButton/CartButton";
import { PriceSelect } from "../../PriceSelect/PriceSelect";
import { usePriceSelectState } from "../../PriceSelect/usePriceSelectState";
import { getProductModalHash } from "../../ProductDetails/ProductModal";

type Props = {
  onSelect: ((wheelId: string) => void) | null;
  part: WheelPartItem | null;
  selected: boolean;
  vehicleId?: string;
};

export const WheelTile: FunctionComponent<Props> = ({
  onSelect,
  part,
  selected,
  vehicleId,
}) => {
  const [language] = useLanguage();
  const { t } = useTranslation();
  const [orderItem] = useRelatedCartItems(part, false);
  const [priceEntries, setPriceEntries] = usePriceSelectState([]);

  const wheelId = part?.partDetails.primaryPart.itemId;
  const detailsLink = getProductModalHash(wheelId);

  return (
    <figure
      className={clsx("tw-weel-tile", selected && "tw-weel-tile--selected")}
    >
      <div
        onClick={(): void => {
          wheelId && onSelect && onSelect(wheelId);
        }}
        className="grid grid-cols-3 gap-4 p-4 md:p-6 md:gap-6 flex-1"
        role={onSelect ? "button" : undefined}
      >
        <Image
          alt="product image"
          className="self-center"
          errorClassName="opacity-20 blur-[8px]"
          fallbackSrc={getPartFallbackSrc(partTypeWheel)}
          pulsate={!part}
          src={part?.partDetails.primaryPart.wheelImageLink}
        />
        <div className="col-span-2 flex flex-col space-y-3 justify-between">
          <h2 className="text-lg leading-4 lg:leading-5 lg:text-xl md:font-medium">
            <Placeholder className="w-24" loading={!part}>
              {getPartName(part, language)}
            </Placeholder>
          </h2>
          <div className="flex flex-wrap text-xs md:text-sm gap-x-4 gap-y-3 items-center">
            <KeyValue>
              <Placeholder className="w-[3em]" loading={!part}>
                {t("searchResults.partNumber")}:
              </Placeholder>
              <Placeholder className="w-[3.6em]" loading={!part}>
                {part?.partDetails.primaryPart.wheelPartNumber}
              </Placeholder>
            </KeyValue>
            <KeyValue>
              <Placeholder className="w-[4em]" loading={!part}>
                {t("searchResults.diameter")}:
              </Placeholder>
              <Placeholder className="w-[2em]" loading={!part}>
                {part?.partDetails.primaryPart.wheelDiameter}
              </Placeholder>
            </KeyValue>
            <PriceSelect
              disabled={!!orderItem}
              isStaggered={false}
              onChange={setPriceEntries}
              onReady={setPriceEntries}
              orderItem={orderItem}
              part={part}
              totalClassName="text-lg"
            />
          </div>
        </div>
      </div>
      <nav
        className={clsx(
          "tw-weel-tile__actions",
          selected && "tw-weel-tile__actions--selected"
        )}
      >
        <Link to={detailsLink}>
          <Button
            aria-label="viewDetails"
            className="rounded-none h-14 flex-1 font-light md:text-base text-right justify-end"
            disabled={!part}
            element="a"
          >
            <InfoIcon />
            {t("searchResults.viewDetails")}
          </Button>
        </Link>
        <div className="border-r border-inherit h-5" />
        <CartButton
          className="rounded-none h-14 flex-1  font-light md:text-base text-left justify-start"
          createPackageClassName={null}
          isStaggered={false}
          part={part}
          pricing={priceEntries}
          vehicleId={Number(vehicleId)}
          vehicleInfo={null}
        />
      </nav>
    </figure>
  );
};
